/**
 * "Change plan" as data (PlanCompare.jsx): the comparison rows, each plan's "What you get" against the person's plan,
 * and what a change means (the confirmation). Reads the gateway's GET /api/v1/me/plans answer
 * (vfs-service src/gateway/admin/planCatalog.js): `{ current, credits, change, pending, plans: [{ name, current,
 * limits, features, containers, backups, tabs, costPerHour, monthlyCredits, monthlyEstimate, capabilities, vsCurrent,
 * canChange, how, requested }] }`. Credits: 1 credit = EUR 0.01 (a sample), vfs-service docs/BILLING-CREDITS.md.
 */
import { formatBytes } from '../../services/SandboxService';
import { TAB_ORDER, capabilityText, planText } from './planCompareText';

const num = (v) => typeof v === 'number' && Number.isFinite(v);
const decimal = (n) => Number(n).toLocaleString('en', { maximumFractionDigits: 2 });

/** "12.5 credits/h", "No estimate" (unlimited cpu or memory: the gateway gives null). */
export function costText(cost, t) {
  return cost && num(cost.credits) ? t('perHour', { n: decimal(cost.credits) }) : t('noEstimate');
}

const limitValue = (key, format) => (plan, t) => {
  const value = plan.limits?.[key];
  if (value === undefined) { return { text: '—' }; }
  return { text: value === null ? t('unlimited') : format(value, t) };
};
const flag = (on) => () => ({ check: Boolean(on) });

/**
 * The rows of the table, top to bottom: `{ key, label, hint?, cell(plan, t) -> { text } | { check } }`. A check row
 * shows ✓ or —; a value row its text. Tabs: how many of the project tabs the plan shows, the names in `title`.
 */
export const COMPARE_ROWS = [
  { key: 'cost', label: 'rowCost', hint: 'rowCostHint', cell: (plan, t) => ({ text: costText(plan.costPerHour, t), strong: true }) },
  {
    key: 'monthlyCredits',
    label: 'rowMonthly',
    cell: (plan, t) => ({
      text: num(plan.monthlyCredits) ? t('monthlyCredits', { n: Math.round(plan.monthlyCredits).toLocaleString('en') }) : t('monthlyNone'),
      title: num(plan.monthlyEstimate?.credits) ? t('rowMonthlyHint', { n: Math.round(plan.monthlyEstimate.credits).toLocaleString('en') }) : undefined,
    }),
  },
  { key: 'cpu', label: 'rowCpu', cell: limitValue('cpu', (v, t) => t('vcpu', { n: decimal(v) })) },
  { key: 'memoryBytes', label: 'rowMemory', cell: limitValue('memoryBytes', (v) => formatBytes(v)) },
  { key: 'diskBytes', label: 'rowDisk', cell: limitValue('diskBytes', (v) => formatBytes(v)) },
  { key: 'dataDiskBytes', label: 'rowDataDisk', cell: limitValue('dataDiskBytes', (v) => formatBytes(v)) },
  { key: 'runningServices', label: 'rowServices', cell: limitValue('runningServices', (v) => String(v)) },
  { key: 'activeHoursPerMonth', label: 'rowHours', cell: limitValue('activeHoursPerMonth', (v, t) => t('hours', { n: decimal(v) })) },
  {
    key: 'tabs',
    label: 'rowTabs',
    cell: (plan, t) => {
      const hidden = new Set(plan.tabs?.hidden || []);
      const shown = TAB_ORDER.filter((id) => !hidden.has(id));
      return { text: t('tabsCount', { n: shown.length, total: TAB_ORDER.length }), tabs: shown };
    },
  },
  { key: 'chat', label: 'rowChat', cell: (plan) => flag(plan.features?.chatOrgModels || plan.features?.chatOwnKey)() },
  { key: 'terminal', label: 'rowTerminal', cell: (plan) => flag(plan.features?.terminal)() },
  { key: 'docker', label: 'rowDocker', cell: (plan) => flag(plan.containers)() },
  {
    key: 'backups',
    label: 'rowBackups',
    cell: (plan, t) => (plan.backups?.manualPerDay > 0 ? { text: t('backupsPerDay', { n: plan.backups.manualPerDay }), check: true } : { check: false }),
  },
];

/** "a, b and 3 more" — at most `max` items named. */
export function shortList(items, t, max = 3) {
  if (items.length <= max) { return items.join(', '); }
  return t('andMore', { list: items.slice(0, max).join(', '), n: items.length - max });
}

/** Tabs gained/lost collapse into one item ("the Data, Traces tabs" reads badly in Italian: each stays its own). */
const capsText = (caps, lang) => (caps || []).map((cap) => capabilityText(cap, lang));
const limitsText = (keys, t) => (keys || []).map((key) => t(`limit_${key}`));

/**
 * "What you get" for a plan, against the person's: `[{ tone: 'gain'|'loss'|'neutral', text }]`, the gains first.
 * The current plan: one neutral line.
 */
export function whatYouGet(plan, lang) {
  const t = planText(lang);
  if (plan.current) { return [{ tone: 'neutral', text: t('current') }]; }
  const vs = plan.vsCurrent;
  if (!vs) { return [{ tone: 'neutral', text: shortList(capsText(plan.capabilities, lang), t, 4) }]; }
  const lines = [];
  if (vs.gains?.length) { lines.push({ tone: 'gain', text: t('adds', { list: shortList(capsText(vs.gains, lang), t) }) }); }
  if (vs.more?.length) { lines.push({ tone: 'gain', text: t('more', { list: shortList(limitsText(vs.more, t), t) }) }); }
  if (vs.losses?.length) { lines.push({ tone: 'loss', text: t('takesAway', { list: shortList(capsText(vs.losses, lang), t) }) }); }
  if (vs.less?.length) { lines.push({ tone: 'loss', text: t('less', { list: shortList(limitsText(vs.less, t), t) }) }); }
  return lines.length ? lines : [{ tone: 'neutral', text: t('same') }];
}

/**
 * What asking for `plan` means, for the confirmation: the cost line (from → to, the difference), what the person
 * gets, and everything that shrinks or goes away (each limit with its from → to, each capability lost).
 */
export function changeSummary(plan, catalog, lang) {
  const t = planText(lang);
  const from = catalog?.current?.costPerHour;
  const to = plan.costPerHour;
  let cost;
  if (from && num(from.credits) && to && num(to.credits)) {
    const delta = to.credits - from.credits;
    cost = t('confirmCost', { from: decimal(from.credits), to: decimal(to.credits), delta: `${delta >= 0 ? '+' : '−'}${decimal(Math.abs(delta))}` });
  } else {
    cost = t('confirmCostUnknown', { to: costText(to, t) });
  }
  const vs = plan.vsCurrent || {};
  const currentLimits = catalog?.current?.limits || {};
  const rowOf = Object.fromEntries(COMPARE_ROWS.map((row) => [row.key, row]));
  const shrinks = [
    ...(vs.less || []).map((key) => {
      const row = rowOf[key];
      if (!row) { return t(`limit_${key}`); }
      const before = row.cell({ ...plan, limits: currentLimits }, t).text;
      const after = row.cell(plan, t).text;
      return `${t(row.label)}: ${before} → ${after}`;
    }),
    ...capsText(vs.losses, lang),
  ].map((line) => line.charAt(0).toUpperCase() + line.slice(1));
  return {
    cost,
    gains: capsText(vs.gains, lang),
    shrinks,
    replaces: catalog?.pending && catalog.pending.to !== plan.name ? catalog.pending.to : null,
  };
}
